import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Lock, Grid3x3, Hexagon, PartyPopper, ArrowRight, Puzzle, Image, LayoutGrid, Lightbulb, Share2, Spline, Check, Star, Scale, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TakuzuGame } from "@/components/games/TakuzuGame";
import { RikudoGame } from "@/components/games/RikudoGame";
import { NonogramGame } from "@/components/games/NonogramGame";
import { HitoriGame } from "@/components/games/HitoriGame";
import { AkariGame } from "@/components/games/AkariGame";
import { HashiGame } from "@/components/games/HashiGame";
import { MasyuGame } from "@/components/games/MasyuGame";
import DeuxVerites from "@/components/DeuxVerites";

const TAKUZU_STAGES = [
  { n: 6, givenRatio: 0.5 },
  { n: 8, givenRatio: 0.42 },
  { n: 10, givenRatio: 0.36 },
];
const RIKUDO_STAGES = [
  { R: 1, givenRatio: 0.45 },
  { R: 2, givenRatio: 0.4 },
  { R: 3, givenRatio: 0.34 },
];
const NONOGRAM_STAGES = [{ n: 5 }, { n: 10 }, { n: 15 }];
const HITORI_STAGES = [{ n: 5 }, { n: 7 }, { n: 9 }];
const AKARI_STAGES = [{ n: 7 }, { n: 10 }, { n: 12 }];
const HASHI_STAGES = [{ size: 7, targetIslands: 10 }, { size: 9, targetIslands: 15 }, { size: 11, targetIslands: 20 }];
const MASYU_STAGES = [{ n: 6 }, { n: 8 }, { n: 10 }];

const GAMES = [
  { id: "takuzu", icon: Grid3x3, stages: TAKUZU_STAGES },
  { id: "rikudo", icon: Hexagon, stages: RIKUDO_STAGES },
  { id: "nonogram", icon: Image, stages: NONOGRAM_STAGES },
  { id: "hitori", icon: LayoutGrid, stages: HITORI_STAGES },
  { id: "akari", icon: Lightbulb, stages: AKARI_STAGES },
  { id: "hashi", icon: Share2, stages: HASHI_STAGES },
  { id: "masyu", icon: Spline, stages: MASYU_STAGES },
];

export default function JeuDeLEsprit({ onDiscuss }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlimited = !!(user && user.unlimited);

  const [game, setGame] = useState(null); // "takuzu" | "rikudo"
  const [stage, setStage] = useState(0);
  const [won, setWon] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [progress, setProgress] = useState({});
  const [starAdded, setStarAdded] = useState(false);

  useEffect(() => {
    if (!unlimited) return;
    api.get("/games/progress").then(({ data }) => setProgress(data || {})).catch(() => {});
  }, [unlimited]);

  const startGame = (g, s) => {
    setGame(g);
    setStage(s);
    setWon(false);
    setStarAdded(false);
    setResetKey((k) => k + 1);
  };

  const backToMenu = () => { setGame(null); setWon(false); };

  const handleWin = useCallback(() => {
    setWon(true);
    api.post("/games/solve", { game, stage })
      .then(({ data }) => { setProgress(data.progress || {}); setStarAdded(true); })
      .catch(() => {});
  }, [game, stage]);

  const discuss = () => {
    onDiscuss?.(t(`games.${game}Discuss`));
  };

  const renderGame = () => {
    const cfg = GAMES.find((g) => g.id === game);
    const props = { ...cfg.stages[stage], resetKey, onWin: handleWin };
    if (game === "takuzu") return <TakuzuGame {...props} />;
    if (game === "rikudo") return <RikudoGame {...props} />;
    if (game === "nonogram") return <NonogramGame {...props} />;
    if (game === "hitori") return <HitoriGame {...props} />;
    if (game === "akari") return <AkariGame {...props} />;
    if (game === "hashi") return <HashiGame {...props} />;
    if (game === "masyu") return <MasyuGame {...props} />;
    return null;
  };

  if (game === "twotruths") {
    return (
      <div className="h-full overflow-y-auto scroll-thin" data-testid="jeux-tab">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <DeuxVerites onDiscuss={onDiscuss} onBack={backToMenu} />
        </div>
      </div>
    );
  }

  const twoTruthsCard = (
    <div className="glass frame-pulse rounded-2xl p-7 mb-8" data-testid="game-card-twotruths">
      <div className="flex items-start justify-between mb-5">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Scale className="text-primary" size={22} strokeWidth={1.6} />
        </div>
        <span className="text-[11px] uppercase tracking-[0.2em] text-primary/70">{t("games.landingEyebrow")}</span>
      </div>
      <h3 className="font-display text-2xl mb-2">{t("games.twoTruths.name")}</h3>
      <p className="text-sm text-foreground/55 leading-relaxed mb-5">{t("games.twoTruths.tagline")}</p>
      <button
        onClick={() => setGame("twotruths")}
        data-testid="twotruths-open-btn"
        className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm"
      >
        <Sparkles size={15} /> {t("games.twoTruths.play")}
      </button>
    </div>
  );

  if (!unlimited) {
    return (
      <div className="h-full overflow-y-auto scroll-thin" data-testid="jeux-locked">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4 text-center">{t("games.landingEyebrow")}</p>
          <h2 className="font-display text-gradient-orange text-center text-4xl sm:text-5xl italic mb-3">{t("games.navLabel")}</h2>
          <p className="text-foreground/50 max-w-lg mx-auto text-center mb-10 leading-relaxed">{t("games.chooseGame")}</p>
          {twoTruthsCard}
          <div className="glass rounded-2xl p-8 text-center border border-primary/15">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="text-primary" size={26} />
            </div>
            <p className="text-foreground/60 leading-relaxed mb-3">{t("games.locked")}</p>
            <p className="text-foreground/45 leading-relaxed mb-8">{t("games.lockedDesc")}</p>
            <button
              onClick={() => navigate("/tarifs")}
              data-testid="jeux-upgrade-btn"
              className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm"
            >
              <Puzzle size={16} /> {t("atelier.upgrade")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="jeux-tab">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {!game ? (
          <>
            <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4 text-center">{t("games.landingEyebrow")}</p>
            <h2 className="font-display text-gradient-orange text-center text-4xl sm:text-5xl italic mb-3">{t("games.navLabel")}</h2>
            <p className="text-foreground/50 max-w-lg mx-auto text-center mb-12 leading-relaxed">{t("games.chooseGame")}</p>

            {twoTruthsCard}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {GAMES.map(({ id, icon: Icon, stages }) => {
                const done = progress[id] || [];
                return (
                <div key={id} className="glass frame-pulse rounded-2xl p-7" data-testid={`game-card-${id}`}>
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="text-primary" size={22} strokeWidth={1.6} />
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] text-primary/80" data-testid={`progress-${id}`}>
                      <Star size={12} fill={done.length ? "currentColor" : "none"} /> {done.length}/{stages.length}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl mb-2">{t(`games.${id}Name`)}</h3>
                  <p className="text-sm text-foreground/55 leading-relaxed mb-5">{t(`games.${id}Tagline`)}</p>
                  <div className="flex items-center gap-2">
                    {stages.map((_, i) => {
                      const complete = done.includes(i);
                      return (
                      <button
                        key={i}
                        data-testid={`stage-${id}-${i}`}
                        onClick={() => startGame(id, i)}
                        className={`tab-orange-fade flex-1 h-9 rounded-full border text-xs transition-colors inline-flex items-center justify-center gap-1
                          ${complete ? "is-complete border-primary text-primary" : "border-primary/30 text-primary"}`}
                      >
                        {complete && <Check size={12} />}
                        {t("games.stageLabel", { n: i + 1 })}
                      </button>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <button
                onClick={backToMenu}
                data-testid="jeux-back-btn"
                className="text-sm text-foreground/45 hover:text-primary transition-colors"
              >
                {t("games.back")}
              </button>
              <span className="text-xs uppercase tracking-[0.2em] text-primary/70">
                {t(`games.${game}Name`)} · {t("games.stageLabel", { n: stage + 1 })}
              </span>
            </div>

            {renderGame()}
          </>
        )}
      </div>

      <AnimatePresence>
        {won && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-background/70 backdrop-blur-sm px-6"
            data-testid="jeux-win"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="glass frame-pulse rounded-3xl p-8 max-w-md text-center"
            >
              <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-5">
                <PartyPopper className="text-primary" size={28} />
              </div>
              <h3 className="font-display text-gradient-orange text-3xl italic mb-2">{t("games.win")}</h3>
              <p className="text-foreground/55 leading-relaxed mb-4">{t("games.winDesc")}</p>
              {starAdded && (
                <p className="inline-flex items-center gap-2 text-sm text-primary mb-6" data-testid="jeux-star-added">
                  <Star size={15} fill="currentColor" /> {t("games.starAdded")}
                </p>
              )}
              <button
                onClick={discuss}
                data-testid="jeux-discuss-btn"
                className="btn-frost-orange w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm mb-3"
              >
                {t("games.understand")} <ArrowRight size={15} />
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setWon(false); setStarAdded(false); setResetKey((k) => k + 1); }}
                  data-testid="jeux-again-btn"
                  className="flex-1 py-2.5 rounded-full border border-white/15 text-foreground/60 text-sm hover:text-foreground transition-colors"
                >
                  {t("games.newGrid")}
                </button>
                <button
                  onClick={backToMenu}
                  className="flex-1 py-2.5 rounded-full border border-white/15 text-foreground/60 text-sm hover:text-foreground transition-colors"
                >
                  {t("games.back")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
